import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  AppBar,
  Toolbar,
  Link,
  Chip,
  Card,
  CardContent,
  Alert,
  Menu,
  MenuItem,
  Popover,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyIcon from '@mui/icons-material/Key';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useAuth } from '@/contexts/AuthContext';
import { SUPPORTED_LANGUAGES } from '@/locales/i18n';
import { trackEvent } from '@/utils/analytics';
import axios from 'axios';

export default function LandingPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { isAuthenticated } = useAuth();
  const [authCode, setAuthCode] = useState('');
  const [authError, setAuthError] = useState('');
  const [isAuthLoading, setIsAuthLoading] = useState(false);
  const [contactForm, setContactForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [contactStatus, setContactStatus] = useState({ sending: false, sent: false, error: '' });
  const [langAnchorEl, setLangAnchorEl] = useState(null);
  const [sitesAnchorEl, setSitesAnchorEl] = useState(null);

  const currentLang = SUPPORTED_LANGUAGES.find(l => l.code === i18n.language) || SUPPORTED_LANGUAGES[0];

  const handleLanguageChange = (langCode) => {
    i18n.changeLanguage(langCode);
    localStorage.setItem('language', langCode);
    setLangAnchorEl(null);
  };

  // 이미 로그인된 경우 대시보드로
  if (isAuthenticated) {
    navigate('/dashboard', { replace: true });
    return null;
  }

  const isCodeValid = authCode.length === 128;

  const handleAuthCodeSubmit = async (e) => {
    e.preventDefault();
    setAuthError('');

    if (!authCode.trim()) {
      return;
    }

    if (authCode.length !== 128) {
      setAuthError(t('landing.auth.authCodeInvalid'));
      return;
    }

    setIsAuthLoading(true);

    try {
      const response = await axios.post('/api/auth/login-code', { authCode });

      if (response.data.success) {
        const { token, user } = response.data.data;
        localStorage.setItem('token', token);
        window.location.href = '/dashboard';
      } else {
        setAuthError(response.data.error || t('landing.auth.loginFailed'));
      }
    } catch (err) {
      const message = err.response?.data?.error || t('landing.auth.loginFailed');
      setAuthError(message);
    }

    setIsAuthLoading(false);
  };

  const handleContactSubmit = async () => {
    if (!contactForm.name || !contactForm.email || !contactForm.message) {
      setContactStatus({ sending: false, sent: false, error: t('landing.contact.formRequired') });
      return;
    }
    setContactStatus({ sending: true, sent: false, error: '' });
    try {
      const response = await axios.post('/api/contact/send', contactForm);
      if (response.data.success) {
        setContactStatus({ sending: false, sent: true, error: '' });
        setContactForm({ name: '', email: '', subject: '', message: '' });
      } else {
        setContactStatus({ sending: false, sent: false, error: response.data.error });
      }
    } catch {
      setContactStatus({ sending: false, sent: false, error: t('landing.contact.sendFailed') });
    }
  };

  const faqItems = [
    {
      question: t('landing.faq.q1'),
      answer: t('landing.faq.a1'),
    },
    {
      question: t('landing.faq.q2'),
      answer: t('landing.faq.a2'),
    },
    {
      question: t('landing.faq.q3'),
      answer: t('landing.faq.a3'),
    },
    {
      question: t('landing.faq.q4'),
      answer: t('landing.faq.a4'),
    },
  ];

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Navigation Bar */}
      <AppBar
        position="sticky"
        elevation={0}
        sx={{ backgroundColor: '#1a1a1a', color: 'white' }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', minHeight: { xs: 56, sm: 64 } }}>
          {/* Left: Logo */}
          <Box
            component="img"
            src="/logo.png"
            alt="주식회사 케이지네트워크"
            sx={{ height: 40, objectFit: 'contain' }}
          />

          {/* Center: Navigation Links */}
          <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 3, alignItems: 'center' }}>
            <Button
              variant="contained"
              size="small"
              onClick={() => {
                trackEvent('try_start', { method: 'nav_cta' });
                navigate('/try');
              }}
              sx={{
                bgcolor: '#f5a623',
                color: '#1a1a1a',
                fontWeight: 800,
                textTransform: 'none',
                '&:hover': { bgcolor: '#e09510' },
              }}
            >
              {t('landing.hero.tryCta')}
            </Button>
            <Link
              href="/#/community"
              underline="none"
              sx={{ color: 'white', fontWeight: 500, fontSize: '0.9rem', '&:hover': { color: 'rgba(255,255,255,0.8)' } }}
            >
              {t('landing.nav.community')}
            </Link>
            <Link
              href="/#/blog"
              underline="none"
              sx={{ color: 'white', fontWeight: 500, fontSize: '0.9rem', '&:hover': { color: 'rgba(255,255,255,0.8)' } }}
            >
              {t('landing.nav.blog')}
            </Link>
            <Typography
              onClick={(e) => setSitesAnchorEl(e.currentTarget)}
              sx={{ color: 'white', fontWeight: 500, fontSize: '0.9rem', cursor: 'pointer', '&:hover': { color: 'rgba(255,255,255,0.8)' } }}
            >
              Our Sites
            </Typography>
            <Popover
              open={Boolean(sitesAnchorEl)}
              anchorEl={sitesAnchorEl}
              onClose={() => setSitesAnchorEl(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
              transformOrigin={{ vertical: 'top', horizontal: 'center' }}
              PaperProps={{ sx: { bgcolor: '#1a1a1a', border: '1px solid #333', mt: 1, p: 1.5 } }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 180 }}>
                <Link href="http://kznetwork.co.kr/" target="_blank" rel="noopener noreferrer" underline="hover" sx={{ color: '#ccc', fontSize: '0.85rem', px: 1, py: 0.5, borderRadius: 1, '&:hover': { color: '#fff', bgcolor: '#333' } }}>
                  kznetwork.co.kr
                </Link>
                <Link href="https://brandgrowth.store/" target="_blank" rel="noopener noreferrer" underline="hover" sx={{ color: '#ccc', fontSize: '0.85rem', px: 1, py: 0.5, borderRadius: 1, '&:hover': { color: '#fff', bgcolor: '#333' } }}>
                  brandgrowth.store
                </Link>
                <Link href="https://aiquote-generator.pages.dev/" target="_blank" rel="noopener noreferrer" underline="hover" sx={{ color: '#ccc', fontSize: '0.85rem', px: 1, py: 0.5, borderRadius: 1, '&:hover': { color: '#fff', bgcolor: '#333' } }}>
                  AI 견적 생성기
                </Link>
              </Box>
            </Popover>
          </Box>

          {/* Right: Language Selector */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography
              variant="body2"
              onClick={(e) => setLangAnchorEl(e.currentTarget)}
              sx={{ color: 'white', fontWeight: 600, cursor: 'pointer', fontSize: '0.9rem', '&:hover': { opacity: 0.8 } }}
            >
              {currentLang.code.toUpperCase()}
            </Typography>
            <Menu
              anchorEl={langAnchorEl}
              open={Boolean(langAnchorEl)}
              onClose={() => setLangAnchorEl(null)}
            >
              {SUPPORTED_LANGUAGES.map((lang) => (
                <MenuItem
                  key={lang.code}
                  onClick={() => handleLanguageChange(lang.code)}
                  selected={lang.code === i18n.language}
                >
                  {lang.flag} {lang.name}
                </MenuItem>
              ))}
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Hero Section — 60초 체험 전면 */}
      <Box
        sx={{
          py: { xs: 5, md: 8 },
          background: 'linear-gradient(180deg, #eef5ff 0%, #ffffff 55%)',
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            align="center"
            sx={{ fontWeight: 800, mb: 1, fontSize: { xs: '1.75rem', md: '2.5rem' } }}
          >
            {t('landing.hero.title')}
          </Typography>
          <Typography
            align="center"
            sx={{ color: 'primary.main', mb: 3, fontStyle: 'italic' }}
          >
            {t('landing.hero.subtitle')}
          </Typography>

          {/* Primary CTA */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 1.5,
                width: '100%',
                maxWidth: 720,
                justifyContent: 'center',
                alignItems: 'stretch',
              }}
            >
              <Button
                variant="contained"
                size="large"
                startIcon={<RocketLaunchIcon />}
                onClick={() => {
                  trackEvent('try_start', { method: 'landing_cta' });
                  navigate('/try');
                }}
                sx={{
                  px: { xs: 3, md: 4 },
                  py: 2,
                  fontSize: { xs: '1rem', md: '1.2rem' },
                  fontWeight: 800,
                  borderRadius: 3,
                  boxShadow: '0 8px 28px rgba(25, 118, 210, 0.35)',
                  textTransform: 'none',
                  flex: 1,
                  minWidth: { xs: '100%', sm: 0 },
                }}
              >
                {t('landing.hero.tryCta')}
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => {
                  trackEvent('lite_start', { method: 'landing_cta' });
                  window.location.href = '/lite/';
                }}
                sx={{
                  px: { xs: 3, md: 4 },
                  py: 2,
                  fontSize: { xs: '1rem', md: '1.2rem' },
                  fontWeight: 800,
                  borderRadius: 3,
                  borderWidth: 2,
                  borderColor: '#244d3b',
                  color: '#244d3b',
                  textTransform: 'none',
                  flex: 1,
                  minWidth: { xs: '100%', sm: 0 },
                  '&:hover': {
                    borderWidth: 2,
                    borderColor: '#183d31',
                    bgcolor: 'rgba(36, 77, 59, 0.06)',
                  },
                }}
              >
                {t('landing.hero.liteCta')}
              </Button>
            </Box>
            <Button
              variant="text"
              onClick={() => {
                trackEvent('try_start', { method: 'landing_sample' });
                navigate('/try?sample=1');
              }}
              sx={{ mt: 1.5, textTransform: 'none', fontWeight: 600 }}
            >
              {t('landing.hero.sampleCta')}
            </Button>
            <Link
              href="mailto:kz4network@gmail.com?subject=koai%20온프레미스%20보안%20상담"
              underline="hover"
              sx={{
                mt: 1,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                color: 'text.secondary',
                fontSize: '0.85rem',
              }}
            >
              <LockOutlinedIcon sx={{ fontSize: 16 }} />
              {t('landing.hero.onpremCta')}
            </Link>
          </Box>

          {/* 3 steps */}
          <Typography align="center" sx={{ fontWeight: 700, mb: 1.5, mt: 2 }}>
            {t('landing.hero.stepsTitle')}
          </Typography>
          <Grid container spacing={2} sx={{ mb: 5, maxWidth: 720, mx: 'auto' }}>
            {[
              t('landing.hero.step1'),
              t('landing.hero.step2'),
              t('landing.hero.step3'),
            ].map((label, i) => (
              <Grid item xs={12} sm={4} key={label}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    textAlign: 'center',
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'primary.light',
                    bgcolor: 'rgba(25,118,210,0.04)',
                  }}
                >
                  <Typography
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      bgcolor: 'primary.main',
                      color: 'white',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 800,
                      fontSize: 14,
                      mb: 1,
                    }}
                  >
                    {i + 1}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {label}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          <Grid container spacing={6} alignItems="center">
            {/* Left - Intro Image */}
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                }}
              >
                <Box
                  component="img"
                  src="/hero-document.png"
                  alt={t('landing.hero.imageAlt')}
                  sx={{
                    width: '100%',
                    maxWidth: '100%',
                    borderRadius: 3,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  }}
                />
                <Typography
                  variant="body2"
                  align="center"
                  sx={{ mt: 2, color: 'text.secondary' }}
                >
                  {t('landing.hero.imageCaption')}
                </Typography>
              </Box>
            </Grid>

            {/* Right - Auth Code Entry (보조) */}
            <Grid item xs={12} md={6}>
              <Paper
                elevation={1}
                sx={{ p: 4, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  {t('landing.hero.hasAccount')}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <KeyIcon sx={{ color: '#f5a623' }} />
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {t('landing.auth.startNow')}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  {t('landing.auth.authCodeDesc')}
                </Typography>

                {authError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {authError}
                  </Alert>
                )}

                <Box component="form" onSubmit={handleAuthCodeSubmit}>
                  <TextField
                    fullWidth
                    placeholder={t('landing.auth.authCodePlaceholder')}
                    value={authCode}
                    onChange={(e) => setAuthCode(e.target.value)}
                    variant="outlined"
                    sx={{ mb: 1 }}
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      {authCode.length}/128
                    </Typography>
                    {isCodeValid && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CheckCircleIcon sx={{ fontSize: 16, color: '#2e7d32' }} />
                        <Typography variant="caption" color="success.main">
                          {t('landing.auth.pressEnter')}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {isCodeValid && (
                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      size="large"
                      disabled={isAuthLoading}
                      sx={{
                        mb: 2,
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #5a6fd6 0%, #6a4190 100%)',
                        },
                      }}
                    >
                      {isAuthLoading ? t('landing.auth.loggingIn') : t('landing.auth.start')}
                    </Button>
                  )}
                </Box>

                <Box sx={{ textAlign: 'center', mb: 2 }}>
                  <Link
                    component="button"
                    variant="body2"
                    onClick={() => navigate('/forgot-password')}
                    sx={{ cursor: 'pointer' }}
                  >
                    {t('landing.auth.forgotCode')}
                  </Link>
                </Box>

                <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid #eee' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    {t('landing.auth.newUser')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {t('landing.auth.newUserDesc')}
                  </Typography>
                  <Button
                    fullWidth
                    variant="outlined"
                    size="large"
                    onClick={() => navigate('/register')}
                    sx={{ fontWeight: 600 }}
                  >
                    {t('landing.auth.createAccount')}
                  </Button>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* AI 기술 활용 안내 Section */}
      <Box sx={{ py: 4, backgroundColor: '#f0f4ff' }}>
        <Container maxWidth="md">
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 2,
              border: '1px solid #e0e8f5',
              backgroundColor: '#f8faff',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Typography variant="body2" sx={{ color: '#666' }}>ⓘ</Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {t('landing.aiNotice.title')}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('landing.aiNotice.description')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              <Chip
                label={t('landing.aiNotice.koaiChip')}
                size="small"
                sx={{
                  backgroundColor: '#e8f5e9',
                  color: '#2e7d32',
                  fontWeight: 500,
                }}
                icon={<Box component="span" sx={{ pl: 1 }}>🟢</Box>}
              />
              <Chip
                label={t('landing.aiNotice.brandGrowthChip')}
                size="small"
                sx={{
                  backgroundColor: '#fce4ec',
                  color: '#c62828',
                  fontWeight: 500,
                }}
                icon={<Box component="span" sx={{ pl: 1 }}>🔴</Box>}
              />
            </Box>
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                {t('landing.aiNotice.notice1')}
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                {t('landing.aiNotice.notice2')}
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                {t('landing.aiNotice.notice3')}
              </Typography>
            </Box>
          </Paper>
        </Container>
      </Box>

      {/* Pricing Section */}
      <Box id="pricing" sx={{ py: 8, backgroundColor: '#f8f9fa' }}>
        <Container maxWidth="lg">
          <Typography variant="h4" align="center" sx={{ fontWeight: 700, mb: 5 }}>
            {t('landing.pricing.title')}
          </Typography>

          <Grid container spacing={3} justifyContent="center">
            {/* Free Plan */}
            <Grid item xs={12} sm={6} md={4}>
              <Card
                sx={{
                  height: '100%',
                  borderRadius: 3,
                  border: '1px solid #e0e0e0',
                }}
              >
                <CardContent sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    {t('landing.pricing.free')}
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 700, mb: 2 }}>
                    $0
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {t('landing.pricing.freeDesc')}
                  </Typography>
                  <Typography variant="body1">{t('landing.pricing.freeFeature')}</Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Standard Plan */}
            <Grid item xs={12} sm={6} md={4}>
              <Card
                sx={{
                  height: '100%',
                  borderRadius: 3,
                  border: '2px solid',
                  borderColor: 'primary.main',
                  position: 'relative',
                  overflow: 'visible',
                }}
              >
                <Chip
                  label={t('landing.pricing.popular')}
                  color="primary"
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: -12,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    fontWeight: 600,
                  }}
                />
                <CardContent sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    {t('landing.pricing.standard')}
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 700, mb: 2 }}>
                    $5
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {t('landing.pricing.standardDesc')}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>{t('landing.pricing.standardFeature')}</Typography>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() => navigate('/register')}
                    sx={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #5a6fd6 0%, #6a4190 100%)',
                      },
                    }}
                  >
                    {t('landing.pricing.subscribe')}
                  </Button>
                </CardContent>
              </Card>
            </Grid>

            {/* Enterprise Plan */}
            <Grid item xs={12} sm={6} md={4}>
              <Card
                sx={{
                  height: '100%',
                  borderRadius: 3,
                  border: '1px solid #e0e0e0',
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    {t('landing.pricing.enterprise')}
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 700, mb: 2 }}>
                    {t('landing.pricing.enterprisePrice')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {t('landing.pricing.enterpriseDesc')}
                  </Typography>
                  <Box component="ul" sx={{ pl: 2, m: 0 }}>
                    <Typography component="li" variant="body2">{t('landing.pricing.enterpriseFeature1')}</Typography>
                    <Typography component="li" variant="body2">{t('landing.pricing.enterpriseFeature2')}</Typography>
                    <Typography component="li" variant="body2">{t('landing.pricing.enterpriseFeature3')}</Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* FAQ Section */}
      <Box id="faq" sx={{ py: 8, backgroundColor: 'white' }}>
        <Container maxWidth="md">
          <Typography variant="h4" align="center" sx={{ fontWeight: 700, mb: 5 }}>
            {t('landing.faq.title')}
          </Typography>

          {faqItems.map((item, index) => (
            <Accordion
              key={index}
              elevation={0}
              sx={{
                border: '1px solid #e0e0e0',
                mb: 1,
                '&:before': { display: 'none' },
                borderRadius: '8px !important',
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 500 }}>{item.question}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography color="text.secondary">{item.answer}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Container>
      </Box>

      {/* Contact / CTA Section */}
      <Box sx={{ py: 5, px: 2 }}>
        <Container maxWidth="md">
          <Paper
            sx={{
              p: 4,
              borderRadius: 3,
              background: 'linear-gradient(135deg, #1a237e 0%, #4a148c 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'white' }}>
                {t('landing.contact.title')}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', mt: 0.5 }}>
                {t('landing.contact.info')}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              onClick={() => window.location.href = 'mailto:kz4network@gmail.com'}
              sx={{
                color: 'white',
                borderColor: 'rgba(255,255,255,0.7)',
                '&:hover': { borderColor: 'white', backgroundColor: 'rgba(255,255,255,0.1)' },
              }}
            >
              {t('landing.contact.emailButton')}
            </Button>
          </Paper>
        </Container>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          py: 6,
          px: 2,
          backgroundColor: '#0d1117',
          color: 'white',
          mt: 'auto',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Paper
                sx={{
                  p: 3,
                  backgroundColor: 'transparent',
                  border: '1px solid #30363d',
                  borderRadius: 2,
                  height: '100%',
                }}
                elevation={0}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, color: 'white' }}>
                  {t('landing.footer.companyName')}
                </Typography>
                <Typography variant="body2" sx={{ color: '#8b949e', lineHeight: 1.8 }}>
                  {t('landing.footer.companyDesc')}
                </Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2, mb: 1, color: 'white' }}>
                  {t('landing.footer.newsletter')}
                </Typography>
                <Typography variant="body2" sx={{ color: '#8b949e' }}>
                  {t('landing.footer.newsletterDesc')}
                </Typography>
                <Typography variant="body2" sx={{ color: '#f85149', mt: 2, fontSize: '0.8rem' }}>
                  {t('landing.footer.businessNumber')}
                </Typography>
                <Typography variant="body2" sx={{ color: '#f85149', fontSize: '0.8rem' }}>
                  {t('landing.footer.commerceNumber')}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper
                sx={{
                  p: 3,
                  backgroundColor: 'transparent',
                  border: '1px solid #30363d',
                  borderRadius: 2,
                  height: '100%',
                }}
                elevation={0}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, color: 'white' }}>
                  {t('landing.footer.usefulLinks')}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Link href="/#/privacy" underline="hover" sx={{ color: '#58a6ff', fontSize: '0.875rem' }}>
                    {t('landing.footer.privacy')}
                  </Link>
                  <Link href="/#/terms" underline="hover" sx={{ color: '#58a6ff', fontSize: '0.875rem' }}>
                    {t('landing.footer.terms')}
                  </Link>
                  <Link href="/#/ai-terms" underline="hover" sx={{ color: '#58a6ff', fontSize: '0.875rem' }}>
                    {t('landing.footer.aiTerms')}
                  </Link>
                  <Link href="/#/login" underline="hover" sx={{ color: '#58a6ff', fontSize: '0.875rem' }}>
                    {t('landing.footer.account')}
                  </Link>
                </Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 3, mb: 1, color: 'white' }}>
                  {t('landing.footer.contactUs')}
                </Typography>
                <Typography variant="body2" sx={{ color: '#8b949e' }}>
                  {t('landing.footer.address')}
                </Typography>
                <Typography variant="body2" sx={{ color: '#8b949e' }}>
                  📞 032)351-9117
                </Typography>
                <Typography variant="body2" sx={{ color: '#8b949e' }}>
                  ✉️ kz4network@gmail.com
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper
                sx={{
                  p: 3,
                  backgroundColor: 'transparent',
                  border: '1px solid #30363d',
                  borderRadius: 2,
                  height: '100%',
                }}
                elevation={0}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, color: 'white' }}>
                  {t('landing.footer.sendUsMessage')}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {contactStatus.sent && (
                    <Alert severity="success" sx={{ mb: 1 }}>{t('landing.contact.sent')}</Alert>
                  )}
                  {contactStatus.error && (
                    <Alert severity="error" sx={{ mb: 1 }}>{contactStatus.error}</Alert>
                  )}
                  <TextField
                    placeholder="Your Name"
                    size="small"
                    value={contactForm.name}
                    onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#161b22',
                        color: 'white',
                        '& fieldset': { borderColor: '#30363d' },
                      },
                    }}
                  />
                  <TextField
                    placeholder="Your Email"
                    size="small"
                    value={contactForm.email}
                    onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#161b22',
                        color: 'white',
                        '& fieldset': { borderColor: '#30363d' },
                      },
                    }}
                  />
                  <TextField
                    placeholder="Subject"
                    size="small"
                    value={contactForm.subject}
                    onChange={(e) => setContactForm({ ...contactForm, subject: e.target.value })}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#161b22',
                        color: 'white',
                        '& fieldset': { borderColor: '#30363d' },
                      },
                    }}
                  />
                  <TextField
                    placeholder="Message"
                    multiline
                    rows={3}
                    size="small"
                    value={contactForm.message}
                    onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#161b22',
                        color: 'white',
                        '& fieldset': { borderColor: '#30363d' },
                      },
                    }}
                  />
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleContactSubmit}
                    disabled={contactStatus.sending}
                    sx={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #5a6fd6 0%, #6a4190 100%)',
                      },
                    }}
                  >
                    {contactStatus.sending ? t('landing.contact.sending') : t('landing.contact.sendMessage')}
                  </Button>
                </Box>
              </Paper>
            </Grid>
          </Grid>
          <Typography
            variant="body2"
            align="center"
            sx={{ color: '#8b949e', mt: 4, pt: 3, borderTop: '1px solid #30363d' }}
          >
            {t('landing.footer.copyright')}
          </Typography>
        </Container>
      </Box>
    </Box>
  );
}
